import { useCallback, useMemo, useState } from 'react';
import { NotificationType } from '../../components/app/base/Notification/Notification';
import { useSnackbarContext } from '../context/SnackbarContext';
import useErrorMapping from './useErrorMapping';

export type UseHandleAction<T = string> = (value: T) => Promise<{} | undefined>;

type UseHandleActionProps<T extends any = string> = {
  action: UseHandleAction<T>;
  successMessage: string;
  onSuccess?: () => void;
  customOnError?: (error: string) => void;
  onCompleted?: () => void;
};

const useHandleAction = <T extends any = string>({
  action,
  successMessage,
  onSuccess,
  onCompleted,
  customOnError,
}: UseHandleActionProps<T>) => {
  const [isPending, setIsPending] = useState(false);
  const { triggerSnackbar } = useSnackbarContext();
  const { getParsedErrorMessage, triggerSnackbarError } = useErrorMapping();

  const handleAction = useCallback(async (value: T) => {
    try {
      setIsPending(true);
      await action(value);
      triggerSnackbar(successMessage, NotificationType.Success);
      onSuccess?.();
    } catch (error: any) {
      if (customOnError) {
        customOnError(getParsedErrorMessage(error));
      } else {
        triggerSnackbarError(error);
      }
    } finally {
      onCompleted?.();
      setIsPending(false);
    }
  }, []);

  return useMemo(
    () => ({
      handleAction,
      isPending,
    }),
    [isPending],
  );
};

export default useHandleAction;
